import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Badge, Button, Col, Form, Modal, Nav, Row, Spinner, Table,
} from 'react-bootstrap';
import {
  FaArrowLeft, FaCheck, FaEdit, FaTrash, FaPlus, FaWpforms, FaLayerGroup,
  FaListUl, FaInbox, FaFileAlt, FaCheckCircle, FaTimesCircle, FaTag,
  FaChevronDown, FaChevronRight, FaEyeSlash,
} from 'react-icons/fa';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  getFormByGuid, updateFormBasicInfo, deleteForm,
  upsertSection, deleteSection,
  upsertField, deleteField,
  upsertOption, deleteOption,
  getFormSubmissions,
  type FormManagementDetail,
  type FormSectionManagement,
  type FormFieldManagement,
  type UpsertFormSectionRequest,
  type UpsertFormFieldRequest,
  type UpsertFormFieldOptionRequest,
  type FormSubmissionSummary,
} from '@/api/formsManagementApi';
import { apiClient } from '@/services/apiClient';
import './FormsManagement.scss';

// ── Helpers ───────────────────────────────────────────────────────────────────

function getFieldType(configJson?: string): string {
  if (!configJson) return 'Text';
  try {
    const cfg = JSON.parse(configJson);
    return cfg?.fieldtype ?? cfg?.type ?? cfg?.fieldType ?? 'Text';
  } catch { return 'Text'; }
}

const FIELD_TYPE_COLORS: Record<string, string> = {
  Choice: 'primary', Text: 'secondary', Rating: 'warning',
  Date: 'info', Ranking: 'success', Likert: 'danger',
  Signature: 'dark', Default: 'secondary',
};

function fieldTypeBadge(type: string) {
  const color = FIELD_TYPE_COLORS[type] ?? 'secondary';
  return <Badge bg={color} className="me-1">{type}</Badge>;
}

type ActiveTab = 'basic' | 'sections' | 'submissions' | 'approvals';

// ── Section / Field / Option modal state types ─────────────────────────────

interface SectionModal { open: boolean; isEdit: boolean; data: UpsertFormSectionRequest; }
interface FieldModal   { open: boolean; isEdit: boolean; sectionGuid?: string; data: UpsertFormFieldRequest; }
interface OptionModal  { open: boolean; isEdit: boolean; fieldGuid: string; data: UpsertFormFieldOptionRequest; }

const emptySectionModal = (): SectionModal => ({
  open: false, isEdit: false,
  data: { sortOrder: 0, sectionType: 'Default' },
});
const emptyFieldModal = (sectionGuid?: string): FieldModal => ({
  open: false, isEdit: false, sectionGuid,
  data: { formSectionGuid: sectionGuid, sortOrder: 0, required: 0, formFieldConfig: '{}' },
});
const emptyOptionModal = (fieldGuid: string): OptionModal => ({
  open: false, isEdit: false, fieldGuid,
  data: { formFieldOption: '', fieldType: 'Choice' },
});

// ── Component ─────────────────────────────────────────────────────────────────

const FormDetailPage: React.FC = () => {
  const { formGuid } = useParams<{ formGuid: string }>();
  const navigate = useNavigate();

  const [form, setForm] = useState<FormManagementDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<ActiveTab>('basic');

  // Basic Info edit state
  const [basicEdit, setBasicEdit] = useState({ formTitle: '', formDescription: '', formCompleteMessage: '', formFooter: '' });

  // Expanded sections
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  // Modals
  const [sectionModal, setSectionModal] = useState<SectionModal>(emptySectionModal());
  const [fieldModal, setFieldModal]     = useState<FieldModal>(emptyFieldModal());
  const [optionModal, setOptionModal]   = useState<OptionModal>(emptyOptionModal(''));
  const [deleteTarget, setDeleteTarget] = useState<{ type: string; guid: string; label: string } | null>(null);
  const [deleting, setDeleting]         = useState(false);

  // Submissions
  const [submissions, setSubmissions]   = useState<FormSubmissionSummary[]>([]);
  const [subsLoading, setSubsLoading]   = useState(false);

  // Approval requests (reuse same API as FormApprovalRequests but filtered by formGuid)
  const [approvals, setApprovals]       = useState<any[]>([]);
  const [approvalsLoading, setApprovalsLoading] = useState(false);

  const load = useCallback(async () => {
    if (!formGuid) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getFormByGuid(formGuid);
      setForm(data);
      setBasicEdit({
        formTitle:           data.formTitle ?? '',
        formDescription:     data.formDescription ?? '',
        formCompleteMessage: data.formCompleteMessage ?? '',
        formFooter:          data.formFooter ?? '',
      });
      // expand all sections by default
      setExpandedSections(new Set(data.sections.map(s => s.formSectionGuid)));
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Failed to load form');
    } finally {
      setLoading(false);
    }
  }, [formGuid]);

  useEffect(() => { load(); }, [load]);

  const loadSubmissions = useCallback(async () => {
    if (!formGuid) return;
    setSubsLoading(true);
    try { setSubmissions(await getFormSubmissions(formGuid)); }
    catch { /* silent */ }
    finally { setSubsLoading(false); }
  }, [formGuid]);

  const loadApprovals = useCallback(async () => {
    if (!formGuid) return;
    setApprovalsLoading(true);
    try {
      const res = await apiClient.get<any[]>(`/api/admin/formapprovals?incompleteOnly=false`);
      // filter client-side by formGuid (backend doesn't filter by formGuid yet)
      setApprovals(res.data.filter((a: any) => a.formGuid?.toLowerCase() === formGuid.toLowerCase()));
    } catch { /* silent */ }
    finally { setApprovalsLoading(false); }
  }, [formGuid]);

  useEffect(() => {
    if (activeTab === 'submissions') loadSubmissions();
    if (activeTab === 'approvals')   loadApprovals();
  }, [activeTab, loadSubmissions, loadApprovals]);

  const flash = (msg: string) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(null), 4000);
  };

  // ── Basic Info ─────────────────────────────────────────────────────────────

  const handleSaveBasicInfo = async () => {
    if (!formGuid || !basicEdit.formTitle.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await updateFormBasicInfo(formGuid, {
        formTitle:           basicEdit.formTitle,
        formDescription:     basicEdit.formDescription || undefined,
        formCompleteMessage: basicEdit.formCompleteMessage || undefined,
        formFooter:          basicEdit.formFooter || undefined,
      });
      flash('Form info saved.');
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete (generic) ───────────────────────────────────────────────────────

  const confirmDelete = (type: string, guid: string, label: string) =>
    setDeleteTarget({ type, guid, label });

  const handleDelete = async () => {
    if (!deleteTarget || !formGuid) return;
    setDeleting(true);
    setError(null);
    try {
      const { type, guid } = deleteTarget;
      if (type === 'form') {
        await deleteForm(formGuid);
        navigate('/admin/forms');
        return;
      }
      if (type === 'section') await deleteSection(formGuid, guid);
      if (type === 'field')   await deleteField(formGuid, guid);
      if (type === 'option') {
        const fieldGuid = deleteTarget.guid.split('|')[0];
        const optGuid   = deleteTarget.guid.split('|')[1];
        await deleteOption(formGuid, fieldGuid, optGuid);
      }
      flash('Deleted successfully.');
      setDeleteTarget(null);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  // ── Section Modal ──────────────────────────────────────────────────────────

  const openAddSection = () => setSectionModal({ open: true, isEdit: false, data: { sortOrder: (form?.sections.length ?? 0), sectionType: 'Default' } });
  const openEditSection = (s: FormSectionManagement) => setSectionModal({
    open: true, isEdit: true,
    data: { formSectionGuid: s.formSectionGuid, title: s.title, description: s.description, sortOrder: s.sortOrder, sectionType: s.sectionType, formSectionConfig: s.formSectionConfig, targetNext: s.targetNext, targetPrev: s.targetPrev, buttonNext: s.buttonNext, buttonPrev: s.buttonPrev },
  });

  const handleSaveSection = async () => {
    if (!formGuid) return;
    setSaving(true);
    setError(null);
    try {
      await upsertSection(formGuid, sectionModal.data);
      flash(sectionModal.isEdit ? 'Section updated.' : 'Section created.');
      setSectionModal(emptySectionModal());
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Failed to save section');
    } finally { setSaving(false); }
  };

  // ── Field Modal ────────────────────────────────────────────────────────────

  const openAddField = (sectionGuid?: string) => {
    const sectionFields = form?.sections.find(s => s.formSectionGuid === sectionGuid)?.fields ?? [];
    setFieldModal({ open: true, isEdit: false, sectionGuid, data: { formSectionGuid: sectionGuid, sortOrder: sectionFields.length, required: 0, formFieldConfig: '{"fieldtype":"Text"}' } });
  };
  const openEditField = (f: FormFieldManagement) => setFieldModal({
    open: true, isEdit: true, sectionGuid: f.formSectionGuid,
    data: { formFieldGuid: f.formFieldGuid, formSectionGuid: f.formSectionGuid, title: f.title, subtitle: f.subtitle, sortOrder: f.sortOrder, required: f.required, formFieldConfig: f.formFieldConfig, conditionTarget: f.conditionTarget, condition: f.condition },
  });

  const handleSaveField = async () => {
    if (!formGuid) return;
    setSaving(true);
    setError(null);
    try {
      await upsertField(formGuid, fieldModal.data);
      flash(fieldModal.isEdit ? 'Field updated.' : 'Field created.');
      setFieldModal(emptyFieldModal());
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Failed to save field');
    } finally { setSaving(false); }
  };

  // ── Option Modal ────────────────────────────────────────────────────────────

  const openAddOption = (fieldGuid: string) => setOptionModal({ open: true, isEdit: false, fieldGuid, data: { formFieldOption: '', fieldType: 'Choice' } });
  const openEditOption = (fieldGuid: string, opt: any) => setOptionModal({
    open: true, isEdit: true, fieldGuid,
    data: { formFieldOptionGuid: opt.formFieldOptionGuid, formFieldOption: opt.formFieldOption, fieldType: opt.fieldType },
  });

  const handleSaveOption = async () => {
    if (!formGuid || !optionModal.fieldGuid) return;
    setSaving(true);
    setError(null);
    try {
      await upsertOption(formGuid, optionModal.fieldGuid, optionModal.data);
      flash(optionModal.isEdit ? 'Option updated.' : 'Option created.');
      setOptionModal(emptyOptionModal(''));
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Failed to save option');
    } finally { setSaving(false); }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) return <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>;
  if (!form)   return <Alert variant="danger" className="m-4">Form not found.</Alert>;

  return (
    <div className="form-detail-page container-fluid p-4">
      {/* Breadcrumb */}
      <div className="d-flex align-items-center gap-2 mb-3 text-muted small">
        <Link to="/admin/forms" className="text-decoration-none d-flex align-items-center gap-1">
          <FaArrowLeft /> Forms
        </Link>
        <span>/</span>
        <span className="text-body">{form.formTitle}</span>
      </div>

      {/* Title row */}
      <div className="d-flex align-items-start justify-content-between flex-wrap gap-2 mb-3">
        <div>
          <h4 className="mb-0 d-flex align-items-center gap-2">
            <FaWpforms className="text-primary" />
            {form.formTitle}
          </h4>
          <div className="d-flex gap-2 mt-1">
            {form.isActive
              ? <Badge bg="success"><FaCheckCircle className="me-1" />Active</Badge>
              : <Badge bg="secondary"><FaEyeSlash className="me-1" />Inactive</Badge>}
            <Badge bg="outline-secondary" className="text-muted border">{form.sections.length} sections</Badge>
          </div>
        </div>
        {form.isActive && (
          <Button variant="outline-danger" size="sm" onClick={() => confirmDelete('form', form.formGuid, form.formTitle)}>
            <FaTrash className="me-1" />Deactivate Form
          </Button>
        )}
      </div>

      {error   && <Alert variant="danger"  dismissible onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Tabs */}
      <Nav variant="tabs" className="form-tabs mb-3" activeKey={activeTab} onSelect={k => setActiveTab(k as ActiveTab)}>
        <Nav.Item>
          <Nav.Link eventKey="basic"><FaFileAlt />Basic Info</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="sections"><FaLayerGroup />Sections &amp; Fields</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="submissions"><FaInbox />Submissions</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="approvals"><FaCheckCircle />Approval Requests</Nav.Link>
        </Nav.Item>
      </Nav>

      {/* ── Basic Info Tab ─────────────────────────────────────────────────── */}
      {activeTab === 'basic' && (
        <div style={{ maxWidth: 680 }}>
          <Row className="g-3">
            <Col xs={12}>
              <div className="basic-info-field">
                <Form.Label>Form Title <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  value={basicEdit.formTitle}
                  onChange={e => setBasicEdit(s => ({ ...s, formTitle: e.target.value }))}
                />
              </div>
            </Col>
            <Col xs={12}>
              <div className="basic-info-field">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea" rows={3}
                  placeholder="Brief description shown to administrators"
                  value={basicEdit.formDescription}
                  onChange={e => setBasicEdit(s => ({ ...s, formDescription: e.target.value }))}
                />
              </div>
            </Col>
            <Col xs={12}>
              <div className="basic-info-field">
                <Form.Label>Completion Message <span className="text-muted fw-normal">(HTML supported)</span></Form.Label>
                <Form.Control
                  as="textarea" rows={5}
                  placeholder="Message shown to the user after they submit the form"
                  value={basicEdit.formCompleteMessage}
                  onChange={e => setBasicEdit(s => ({ ...s, formCompleteMessage: e.target.value }))}
                />
              </div>
            </Col>
            <Col xs={12}>
              <div className="basic-info-field">
                <Form.Label>Footer <span className="text-muted fw-normal">(HTML supported)</span></Form.Label>
                <Form.Control
                  as="textarea" rows={3}
                  placeholder="Footer text shown at the bottom of the form"
                  value={basicEdit.formFooter}
                  onChange={e => setBasicEdit(s => ({ ...s, formFooter: e.target.value }))}
                />
              </div>
            </Col>
            <Col xs={12}>
              <Button variant="primary" onClick={handleSaveBasicInfo} disabled={saving || !basicEdit.formTitle.trim()}>
                {saving ? <Spinner size="sm" animation="border" className="me-1" /> : <FaCheck className="me-1" />}
                Save Changes
              </Button>
            </Col>
          </Row>
        </div>
      )}

      {/* ── Sections & Fields Tab ──────────────────────────────────────────── */}
      {activeTab === 'sections' && (
        <div>
          <div className="d-flex justify-content-end mb-3">
            <Button variant="outline-primary" size="sm" onClick={openAddSection}>
              <FaPlus className="me-1" />Add Section
            </Button>
          </div>

          {form.sections.length === 0 ? (
            <div className="text-center text-muted py-5">
              <FaLayerGroup size={28} className="mb-2 opacity-50" />
              <p>No sections yet. Add a section to get started.</p>
            </div>
          ) : (
            <div className="sections-accordion">
              {form.sections.map(section => {
                const expanded = expandedSections.has(section.formSectionGuid);
                return (
                  <div key={section.formSectionGuid} className="section-item">
                    <div
                      className="section-header"
                      onClick={() => setExpandedSections(prev => {
                        const next = new Set(prev);
                        expanded ? next.delete(section.formSectionGuid) : next.add(section.formSectionGuid);
                        return next;
                      })}
                    >
                      <div className="section-order">{section.sortOrder}</div>
                      <span className="section-title">
                        {section.title || <span className="text-muted fst-italic">Untitled Section</span>}
                      </span>
                      <Badge bg="outline-secondary" className="text-muted border">{section.sectionType}</Badge>
                      {!section.isActive && <Badge bg="secondary"><FaEyeSlash className="me-1" />Inactive</Badge>}
                      <span className="text-muted small">{section.fields.length} field{section.fields.length !== 1 ? 's' : ''}</span>
                      <div className="section-actions" onClick={e => e.stopPropagation()}>
                        <Button variant="outline-primary" size="sm" title="Edit section" onClick={() => openEditSection(section)}>
                          <FaEdit />
                        </Button>
                        <Button variant="outline-danger" size="sm" title="Delete section" onClick={() => confirmDelete('section', section.formSectionGuid, section.title ?? 'this section')}>
                          <FaTrash />
                        </Button>
                      </div>
                      <span className="text-muted">{expanded ? <FaChevronDown /> : <FaChevronRight />}</span>
                    </div>

                    {expanded && (
                      <div className="section-body">
                        {section.description && (
                          <p className="text-muted small mb-2">{section.description}</p>
                        )}

                        {section.fields.length === 0 ? (
                          <p className="text-muted small text-center py-2">No fields in this section.</p>
                        ) : (
                          section.fields.map(field => {
                            const fieldType = getFieldType(field.formFieldConfig);
                            const hasOptions = ['Choice', 'Ranking', 'Likert'].includes(fieldType);
                            return (
                              <div key={field.formFieldGuid} className={`field-item ${!field.isActive ? 'inactive' : ''}`}>
                                <span className="field-order">{field.sortOrder}</span>
                                <div className="field-info">
                                  <div className="field-title d-flex align-items-center gap-1 flex-wrap">
                                    {fieldTypeBadge(fieldType)}
                                    {field.title ?? <span className="text-muted fst-italic">Untitled Field</span>}
                                    {field.required === 1 && <Badge bg="danger" className="ms-1">Required</Badge>}
                                    {!field.isActive && <Badge bg="secondary"><FaEyeSlash /></Badge>}
                                  </div>
                                  {field.subtitle && <div className="field-subtitle">{field.subtitle}</div>}
                                  {/* Options */}
                                  {field.options.length > 0 && (
                                    <div className="mt-1">
                                      {field.options.map(opt => (
                                        <span key={opt.formFieldOptionGuid ?? opt.formFieldOption} className="option-chip">
                                          <FaTag className="text-muted" />
                                          {opt.formFieldOption}
                                          <button
                                            className="btn btn-link btn-sm p-0 ms-1 text-muted"
                                            title="Edit option"
                                            onClick={() => openEditOption(field.formFieldGuid, opt)}
                                          >
                                            <FaEdit size={10} />
                                          </button>
                                          <button
                                            className="btn btn-link btn-sm p-0 ms-0 text-danger"
                                            title="Delete option"
                                            onClick={() => confirmDelete('option', `${field.formFieldGuid}|${opt.formFieldOptionGuid}`, opt.formFieldOption)}
                                          >
                                            <FaTimesCircle size={10} />
                                          </button>
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                <div className="field-actions">
                                  {hasOptions && (
                                    <Button variant="outline-secondary" size="sm" title="Add option" onClick={() => openAddOption(field.formFieldGuid)}>
                                      <FaPlus />
                                    </Button>
                                  )}
                                  <Button variant="outline-primary" size="sm" title="Edit field" onClick={() => openEditField(field)}>
                                    <FaEdit />
                                  </Button>
                                  <Button variant="outline-danger" size="sm" title="Delete field" onClick={() => confirmDelete('field', field.formFieldGuid, field.title ?? 'this field')}>
                                    <FaTrash />
                                  </Button>
                                </div>
                              </div>
                            );
                          })
                        )}

                        <Button variant="outline-secondary" size="sm" className="mt-2" onClick={() => openAddField(section.formSectionGuid)}>
                          <FaPlus className="me-1" />Add Field
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Submissions Tab ────────────────────────────────────────────────── */}
      {activeTab === 'submissions' && (
        <div>
          {subsLoading ? (
            <div className="text-center py-4"><Spinner animation="border" variant="primary" /></div>
          ) : submissions.length === 0 ? (
            <div className="text-center text-muted py-5">
              <FaInbox size={28} className="mb-2 opacity-50" /><p>No submissions yet.</p>
            </div>
          ) : (
            <>
              <p className="text-muted small mb-2">{submissions.length} submission{submissions.length !== 1 ? 's' : ''}</p>
              <div className="table-responsive">
                <Table hover size="sm" className="submissions-table">
                  <thead>
                    <tr>
                      <th>Submitted By</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Department</th>
                      <th>Status</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {submissions.map(s => (
                      <tr key={s.formSubmittedGuid ?? s.createUser + s.createDt}>
                        <td>{s.createUser}</td>
                        <td>
                          {s.submittedFirstName || s.submittedLastName
                            ? `${s.submittedFirstName ?? ''} ${s.submittedLastName ?? ''}`.trim()
                            : <span className="text-muted fst-italic">—</span>}
                        </td>
                        <td>{s.submittedEmail ?? <span className="text-muted fst-italic">—</span>}</td>
                        <td>{s.submittedOrgStructureDept ?? s.submittedOrgStructure ?? <span className="text-muted fst-italic">—</span>}</td>
                        <td>
                          <Badge bg={s.formSubmittedStatus === 'Submitted' ? 'success' : 'secondary'}>
                            {s.formSubmittedStatus || 'Unknown'}
                          </Badge>
                        </td>
                        <td>{new Date(s.createDt).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Approval Requests Tab ──────────────────────────────────────────── */}
      {activeTab === 'approvals' && (
        <div>
          <div className="d-flex justify-content-end mb-3">
            <Link
              to={`/admin/form-approvals?formGuid=${formGuid}`}
              className="btn btn-sm btn-outline-primary"
            >
              Manage in Form Approvals →
            </Link>
          </div>
          {approvalsLoading ? (
            <div className="text-center py-4"><Spinner animation="border" variant="primary" /></div>
          ) : approvals.length === 0 ? (
            <div className="text-center text-muted py-5">
              <FaCheckCircle size={28} className="mb-2 opacity-50" /><p>No approval requests for this form.</p>
            </div>
          ) : (
            <>
              <p className="text-muted small mb-2">{approvals.length} request{approvals.length !== 1 ? 's' : ''}</p>
              <div className="table-responsive">
                <Table hover size="sm" className="approvals-table">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Submitted By</th>
                      <th>Email</th>
                      <th>Response</th>
                      <th>Sent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {approvals.map(a => (
                      <tr key={a.formGuid ?? a.formApprovalRequestId}>
                        <td className="text-truncate" style={{ maxWidth: 200 }}>{a.formApprovalRequestTitle ?? '—'}</td>
                        <td>{a.formSubmittedNtid ?? '—'}</td>
                        <td>{a.formSubmittedEmail ?? <Badge bg="warning" text="dark">Missing</Badge>}</td>
                        <td>{a.formApprovalResponse ?? <span className="text-muted fst-italic">Pending</span>}</td>
                        <td>{a.formApprovalRequestSentDate ? new Date(a.formApprovalRequestSentDate).toLocaleString() : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Section Modal ──────────────────────────────────────────────────── */}
      <Modal show={sectionModal.open} onHide={() => setSectionModal(emptySectionModal())} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FaLayerGroup className="me-2" />
            {sectionModal.isEdit ? 'Edit Section' : 'Add Section'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col sm={8}>
              <Form.Label>Title</Form.Label>
              <Form.Control
                autoFocus
                value={sectionModal.data.title ?? ''}
                onChange={e => setSectionModal(m => ({ ...m, data: { ...m.data, title: e.target.value } }))}
                placeholder="Section title"
              />
            </Col>
            <Col sm={4}>
              <Form.Label>Sort Order</Form.Label>
              <Form.Control
                type="number"
                value={sectionModal.data.sortOrder}
                onChange={e => setSectionModal(m => ({ ...m, data: { ...m.data, sortOrder: +e.target.value } }))}
              />
            </Col>
            <Col xs={12}>
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea" rows={2}
                value={sectionModal.data.description ?? ''}
                onChange={e => setSectionModal(m => ({ ...m, data: { ...m.data, description: e.target.value } }))}
                placeholder="Optional description shown above section fields"
              />
            </Col>
            <Col sm={6}>
              <Form.Label>Section Type</Form.Label>
              <Form.Select
                value={sectionModal.data.sectionType}
                onChange={e => setSectionModal(m => ({ ...m, data: { ...m.data, sectionType: e.target.value } }))}
              >
                <option value="Default">Default</option>
                <option value="Wizard">Wizard (Step)</option>
                <option value="Conditional">Conditional</option>
              </Form.Select>
            </Col>
            <Col sm={3}>
              <Form.Label>Next Button</Form.Label>
              <Form.Control
                value={sectionModal.data.buttonNext ?? ''}
                onChange={e => setSectionModal(m => ({ ...m, data: { ...m.data, buttonNext: e.target.value } }))}
                placeholder="Next"
              />
            </Col>
            <Col sm={3}>
              <Form.Label>Prev Button</Form.Label>
              <Form.Control
                value={sectionModal.data.buttonPrev ?? ''}
                onChange={e => setSectionModal(m => ({ ...m, data: { ...m.data, buttonPrev: e.target.value } }))}
                placeholder="Back"
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setSectionModal(emptySectionModal())} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={handleSaveSection} disabled={saving}>
            {saving ? <Spinner size="sm" animation="border" className="me-1" /> : <FaCheck className="me-1" />}
            {sectionModal.isEdit ? 'Save' : 'Add Section'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Field Modal ────────────────────────────────────────────────────── */}
      <Modal show={fieldModal.open} onHide={() => setFieldModal(emptyFieldModal())} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FaListUl className="me-2" />
            {fieldModal.isEdit ? 'Edit Field' : 'Add Field'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col sm={8}>
              <Form.Label>Field Title</Form.Label>
              <Form.Control
                autoFocus
                value={fieldModal.data.title ?? ''}
                onChange={e => setFieldModal(m => ({ ...m, data: { ...m.data, title: e.target.value } }))}
                placeholder="Question or field label"
              />
            </Col>
            <Col sm={4}>
              <Form.Label>Sort Order</Form.Label>
              <Form.Control
                type="number"
                value={fieldModal.data.sortOrder}
                onChange={e => setFieldModal(m => ({ ...m, data: { ...m.data, sortOrder: +e.target.value } }))}
              />
            </Col>
            <Col xs={12}>
              <Form.Label>Subtitle / Help Text</Form.Label>
              <Form.Control
                value={fieldModal.data.subtitle ?? ''}
                onChange={e => setFieldModal(m => ({ ...m, data: { ...m.data, subtitle: e.target.value } }))}
                placeholder="Optional hint shown below the field label"
              />
            </Col>
            <Col sm={6}>
              <Form.Label>Field Type</Form.Label>
              <Form.Select
                value={(() => { try { return JSON.parse(fieldModal.data.formFieldConfig ?? '{}')?.fieldtype ?? 'Text'; } catch { return 'Text'; } })()}
                onChange={e => setFieldModal(m => ({ ...m, data: { ...m.data, formFieldConfig: JSON.stringify({ fieldtype: e.target.value }) } }))}
              >
                <option value="Text">Text</option>
                <option value="Choice">Choice</option>
                <option value="Rating">Rating (Stars)</option>
                <option value="Date">Date</option>
                <option value="Ranking">Ranking</option>
                <option value="Likert">Likert Scale</option>
                <option value="Signature">Signature</option>
              </Form.Select>
            </Col>
            <Col sm={6}>
              <Form.Label>Required</Form.Label>
              <Form.Select
                value={fieldModal.data.required}
                onChange={e => setFieldModal(m => ({ ...m, data: { ...m.data, required: +e.target.value } }))}
              >
                <option value={0}>No</option>
                <option value={1}>Yes</option>
              </Form.Select>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setFieldModal(emptyFieldModal())} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={handleSaveField} disabled={saving}>
            {saving ? <Spinner size="sm" animation="border" className="me-1" /> : <FaCheck className="me-1" />}
            {fieldModal.isEdit ? 'Save' : 'Add Field'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Option Modal ───────────────────────────────────────────────────── */}
      <Modal show={optionModal.open} onHide={() => setOptionModal(emptyOptionModal(''))}>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaTag className="me-2" />
            {optionModal.isEdit ? 'Edit Option' : 'Add Option'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Option Text <span className="text-danger">*</span></Form.Label>
            <Form.Control
              autoFocus
              value={optionModal.data.formFieldOption}
              onChange={e => setOptionModal(m => ({ ...m, data: { ...m.data, formFieldOption: e.target.value } }))}
              placeholder="e.g. Yes, Option A, Strongly Agree"
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Option Type</Form.Label>
            <Form.Select
              value={optionModal.data.fieldType ?? 'Choice'}
              onChange={e => setOptionModal(m => ({ ...m, data: { ...m.data, fieldType: e.target.value } }))}
            >
              <option value="Choice">Choice</option>
              <option value="Statement">Statement (Likert row)</option>
              <option value="Other">Other (free text)</option>
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setOptionModal(emptyOptionModal(''))} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={handleSaveOption} disabled={saving || !optionModal.data.formFieldOption.trim()}>
            {saving ? <Spinner size="sm" animation="border" className="me-1" /> : <FaCheck className="me-1" />}
            {optionModal.isEdit ? 'Save' : 'Add Option'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Delete Confirm Modal ───────────────────────────────────────────── */}
      <Modal show={!!deleteTarget} onHide={() => setDeleteTarget(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Delete <strong>{deleteTarget?.label}</strong>?
          </p>
          {deleteTarget?.type === 'form' && (
            <p className="text-muted small">The form and all its sections, fields, and options will be soft-deleted.</p>
          )}
          {deleteTarget?.type === 'section' && (
            <p className="text-muted small">The section will be soft-deleted. Fields remain in the database.</p>
          )}
          {deleteTarget?.type === 'field' && (
            <p className="text-muted small">The field and all its options will be soft-deleted.</p>
          )}
          <p className="text-danger small mb-0">This can be recovered from the database if needed.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? <Spinner size="sm" animation="border" className="me-1" /> : <FaTrash className="me-1" />}
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default FormDetailPage;
